import getComments, { CANtrackCommentRaw } from 'api/comments/getComments';
import getRecentComments from 'api/comments/getRecentComments';
import getPinnedCases from 'api/comments/getPinnedCases';
import axiosInstance from 'api/axiosInstance';
import { URL_CANTRACK_READ_CASES } from 'constants/urls';
import type {
  DashboardData,
  MyCaseSummary,
  RecentActivityItem,
  PinnedCase,
  TodayEntry,
  DailyWorkPlanState,
} from 'containers/pages/cantrackDashboard/types';

const DWP_CASE_ID_KEY = 'dwp_caseId';

export function getDwpCaseId(): number | null {
  const stored = localStorage.getItem(DWP_CASE_ID_KEY);
  if (!stored) return null;
  const parsed = parseInt(stored, 10);
  return Number.isNaN(parsed) ? null : parsed;
}

export function setDwpCaseId(caseId: number | null): void {
  if (caseId === null) {
    localStorage.removeItem(DWP_CASE_ID_KEY);
  } else {
    localStorage.setItem(DWP_CASE_ID_KEY, String(caseId));
  }
}

function mapCaseToSummary(raw: any): MyCaseSummary {
  return {
    Id: Number(raw.Id),
    Title: raw.Title ?? '',
    CustomerName: raw.CustomerName ?? '',
    StatusId: Number(raw.StatusId) || 0,
    StatusTitle: raw.StatusTitle ?? '',
    UpdatedOn: raw.UpdatedOn ?? '',
    Description: raw.Description ?? '',
    ConsultantName: raw.ConsultantName ?? '',
    ProjectTitle: raw.ProjectTitle ?? '',
    GroupTitle: raw.GroupTitle ?? '',
    NumberOfActivities: Number(raw.NumberOfActivities) || 0,
    TimeSpent: raw.TimeSpent ?? '0',
  };
}

function mapCommentToActivity(raw: CANtrackCommentRaw): RecentActivityItem {
  return {
    id: Number(raw.Id),
    caseId: Number(raw.CaseId),
    caseTitle: raw.CaseTitle ?? '',
    authorName: raw.CustomerName ?? '',
    authorContactId: 0,
    title: raw.TaskTitle ?? raw.Description ?? '',
    description: raw.Description ?? '',
    date: raw.EntryDate ?? '',
    timeRegular: parseFloat(raw.TimeRegular) || 0,
    timeOver: parseFloat(raw.TimeOver) || 0,
    billable: raw.Billable === 'Y',
  };
}

function mapCommentToEntry(raw: CANtrackCommentRaw): TodayEntry {
  return {
    id: Number(raw.Id),
    caseId: Number(raw.CaseId),
    caseTitle: raw.CaseTitle ?? '',
    activityTitle: raw.ActivityTitle ?? '',
    title: raw.TaskTitle ?? raw.Description ?? '',
    description: raw.Description ?? '',
    timeRegular: parseFloat(raw.TimeRegular) || 0,
    timeOver: parseFloat(raw.TimeOver) || 0,
    billable: raw.Billable === 'Y',
    date: raw.EntryDate ?? '',
  };
}

function mapPinnedCase(raw: any): PinnedCase {
  return {
    Id: Number(raw.Id ?? raw.CaseId),
    Title: raw.Title ?? raw.CaseTitle ?? '',
    CustomerName: raw.CustomerName ?? '',
    StatusId: Number(raw.StatusId) || 0,
    StatusTitle: raw.StatusTitle ?? '',
    UpdatedOn: raw.UpdatedOn ?? '',
  };
}

async function fetchMyCases(employeeId: string): Promise<MyCaseSummary[]> {
  try {
    const payload: Record<string, unknown> = {
      SearchKeyword: '',
      Page: 1,
      Take: 1000,
      ConsultantId: employeeId,
    };
    const { data } = await axiosInstance.get(
      `${URL_CANTRACK_READ_CASES}?payload=${JSON.stringify(payload)}`,
    );
    const cases: any[] = Array.isArray(data) ? data : data?.Data || [];
    return cases.filter(
      (c) => c.StatusTitle !== 'Closed' && c.StatusTitle !== 'Cancelled',
    );
  } catch (error) {
    console.error('[fetchMyCases] Failed for employee:', employeeId, error);
    throw error;
  }
}

async function fetchRecentActivity(): Promise<RecentActivityItem[]> {
  try {
    const result = await getRecentComments({
      Take: 20,
      Page: 1,
    });
    const now = new Date();
    return (result.data || [])
      .map(mapCommentToActivity)
      .filter((item) => !item.date || new Date(item.date) <= now);
  } catch (error) {
    console.error('[fetchRecentActivity] Failed:', error);
    throw error;
  }
}

async function fetchPinnedCases(userId: string): Promise<PinnedCase[]> {
  try {
    const data = await getPinnedCases(Number(userId));
    return (data || []).map(mapPinnedCase);
  } catch (error) {
    console.error('[fetchPinnedCases] Failed for user:', userId, error);
    throw error;
  }
}

async function fetchTodayEntries(employeeId: string): Promise<{ entries: TodayEntry[]; raw: CANtrackCommentRaw[] }> {
  try {
    const today = new Date();
    const rawComments = await getComments(today, employeeId) || [];
    return { entries: rawComments.map(mapCommentToEntry), raw: rawComments };
  } catch (error) {
    console.error('[fetchTodayEntries] Failed for employee:', employeeId, error);
    throw error;
  }
}

function detectDailyWorkPlan(
  todayEntries: TodayEntry[],
  rawComments: CANtrackCommentRaw[],
): DailyWorkPlanState | null {
  const dwpCaseId = getDwpCaseId();
  if (!dwpCaseId) return null;

  const todayComment = todayEntries.find((e) => e.caseId === dwpCaseId);
  const rawMatch = rawComments.find((r) => Number(r.CaseId) === dwpCaseId);

  return {
    caseId: dwpCaseId,
    caseTitle: todayComment?.caseTitle ?? 'Daily Work Plan',
    todayComment: todayComment
      ? {
          id: todayComment.id,
          title: todayComment.title,
          description: todayComment.description || '',
          date: new Date(todayComment.date),
          timeRegular: todayComment.timeRegular,
          timeOver: todayComment.timeOver,
          caseID: todayComment.caseId,
          caseTitle: todayComment.caseTitle,
          customerID: rawMatch ? Number(rawMatch.CustomerId) : 0,
          customerName: rawMatch?.CustomerName ?? '',
          gpda: {
            group: { id: rawMatch ? Number(rawMatch.GroupId) : 0, title: rawMatch?.GroupTitle ?? '' },
            project: { id: rawMatch ? Number(rawMatch.ProjectId) : 0, title: rawMatch?.ProjectTitle ?? '' },
            deliverable: { id: rawMatch ? Number(rawMatch.DeliverableId) : 0, title: rawMatch?.DeliverableTitle ?? '' },
            activity: { id: rawMatch ? Number(rawMatch.ActivityId) : 0, title: rawMatch?.ActivityTitle ?? '' },
          },
          billable: todayComment.billable,
        }
      : null,
    submittedToday: !!todayComment,
  };
}

export async function getDashboardData(
  userId: string,
  contactId: string,
  employeeId: string,
): Promise<DashboardData> {
  try {
    const [myCases, recentActivity, pinnedCases, todayResult] =
      await Promise.all([
        fetchMyCases(employeeId),
        fetchRecentActivity(),
        fetchPinnedCases(userId),
        fetchTodayEntries(employeeId),
      ]);

    const todayEntries = todayResult.entries;
    const dailyWorkPlan = detectDailyWorkPlan(todayEntries, todayResult.raw);

    return {
      myCases,
      recentActivity,
      pinnedCases,
      todayEntries,
      dailyWorkPlan,
    };
  } catch (error) {
    console.error('[getDashboardData] Failed:', error);
    throw error;
  }
}

export default getDashboardData;
